/*
 * A small history router, base-path aware.
 *
 * The app has a list and a workbench, so a routing library would be more
 * configuration than code. What matters here is that the mount prefix is not
 * assumed: paths are stored WITHOUT the base (`/kb/abc/wiki`) and rendered WITH
 * it, so the same bundle works at the domain root and under a sub-path.
 *
 * The tab is part of the URL rather than component state, so a reviewer can be
 * sent straight to the diff they are asking about and the back button steps
 * through tabs the way it steps through anything else.
 */
import { useEffect, useState } from 'react'

/** Mount prefix without a trailing slash — `''` at the domain root. */
const BASE = import.meta.env.BASE_URL.replace(/\/+$/, '')

/** Strip the mount prefix off a browser pathname. */
export function toAppPath(pathname: string): string {
  const stripped = BASE && pathname.startsWith(BASE) ? pathname.slice(BASE.length) : pathname
  return stripped.startsWith('/') ? stripped : `/${stripped}`
}

/** Add the mount prefix back on, for an href or a history entry. */
export function toHref(appPath: string): string {
  const suffix = appPath.startsWith('/') ? appPath : `/${appPath}`
  return `${BASE}${suffix}` || '/'
}

export function navigate(appPath: string): void {
  window.history.pushState(null, '', toHref(appPath))
  window.dispatchEvent(new PopStateEvent('popstate'))
}

/** Current in-app path, kept in sync with back/forward and `navigate`. */
export function useAppPath(): string {
  const [path, setPath] = useState(() => toAppPath(window.location.pathname))
  useEffect(() => {
    const sync = () => setPath(toAppPath(window.location.pathname))
    window.addEventListener('popstate', sync)
    return () => window.removeEventListener('popstate', sync)
  }, [])
  return path
}

/** The workbench tabs, in the order the header renders them. */
export const TABS = [
  'chat',
  'files',
  'wiki',
  'graph',
  'history',
  'schema',
  'activity',
  'limits',
] as const

export type TabId = (typeof TABS)[number]

export const TAB_LABELS: Record<TabId, string> = {
  chat: 'Чат',
  files: 'Файлы',
  wiki: 'Wiki',
  graph: 'Граф',
  history: 'История',
  schema: 'Схема',
  activity: 'Активность',
  limits: 'Лимиты',
}

export type Route = { name: 'list' } | { name: 'workbench'; kbId: string; tab: TabId }

/** `/kb/<id>[/<tab>]` -> the workbench, anything else -> the list. */
export function parseRoute(appPath: string): Route {
  const match = /^\/kb\/([^/]+)(?:\/([^/]+))?\/?$/.exec(appPath)
  if (!match?.[1]) return { name: 'list' }
  const requested = match[2]
  const tab = TABS.find((candidate) => candidate === requested) ?? 'chat'
  return { name: 'workbench', kbId: decodeURIComponent(match[1]), tab }
}

export function workbenchPath(kbId: string, tab: TabId = 'chat'): string {
  return `/kb/${encodeURIComponent(kbId)}/${tab}`
}
